import React, { ReactElement } from "react";
import { IdwalGrade } from "../../IdwalGrades/IdwalGrade";
import { ReportDetailsProps } from "../../types/ReportDetailsProps";
import { Trans } from "react-i18next";
import "./ReportDetails.css";

const LOCALE = "en-GB";

const getDayWithOrdinal = (day: number): JSX.Element => {
    if (day > 3 && day < 21) {
        return (
            <>
                {day}
                <sup>th</sup>
            </>
        );
    }

    let ordinal: string;

    switch (day % 10) {
        case 1:
            ordinal = "st";
            break;
        case 2:
            ordinal = "nd";
            break;
        case 3:
            ordinal = "rd";
            break;
        default:
            ordinal = "th";
            break;
    }

    return (
        <>
            {day}
            <sup>{ordinal}</sup>
        </>
    );
};

const formatDate = (dateString: string) => {
    const date: Date = new Date(dateString);
    const day: number = parseInt(
        date.toLocaleDateString(LOCALE, { day: "2-digit" })
    );
    const dayWithOrdinal: JSX.Element = getDayWithOrdinal(day);

    return (
        <>
            {dayWithOrdinal}{" "}
            {date.toLocaleDateString(LOCALE, {
                year: "numeric",
                month: "long"
            })}
        </>
    );
};

const getDate = (dateFromString: string, dateToString: string): JSX.Element => {
    if (!dateToString) {
        return formatDate(dateFromString);
    }

    return (
        <>
            {formatDate(dateFromString)} - {formatDate(dateToString)}
        </>
    );
};

export const ReportDetails = (props: ReportDetailsProps): ReactElement => {
    const data = props.reportDetails;

    const getLocaleValue = (i18nKey: string) => {
        return (
            <Trans
                ns="common"
                i18nKey={`inspectionReport.reportDetails.${i18nKey}`}
            />
        );
    };

    return (
        <div>
            <div className="grid">
                <div className="col-12 xl:col-6">
                    <p className="report-title">{data.ShipName}</p>
                </div>
                <div className="col-12 xl:col-6 xl:text-right">
                    <IdwalGrade
                        grade={data.Grade}
                        size={"normal"}
                        width={0}
                        height={0}
                        className="idwal-grade xl:text-right"
                    />
                </div>
            </div>
            <table className="report-table mt-5 xl:mt-0">
                <tbody>
                    <tr>
                        <td>{getLocaleValue("inspectionRef")}:</td>
                        <td data-cy={"report-table-inspection-number"}>
                            {data.InspectionNumber}
                        </td>
                    </tr>
                    <tr>
                        <td>{getLocaleValue("inspectionType")}:</td>
                        <td data-cy={"report-table-inspection-type"}>
                            {data.InspectionType}
                        </td>
                    </tr>
                    <tr>
                        <td>{getLocaleValue("inspectedAt")}:</td>
                        <td data-cy={"report-table-inspected-at"}>
                            {data.InspectedAt}
                        </td>
                    </tr>
                    <tr>
                        <td>{getLocaleValue("date")}:</td>
                        <td data-cy={"report-table-inspection-date"}>
                            {getDate(
                                data.InspectedDateFrom,
                                data.InspectedDateTo
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
            <table className="report-table mt-3">
                <tbody>
                    <tr>
                        <td>{getLocaleValue("imoNumber")}:</td>
                        <td data-cy={"report-table-imo"}>{data.IMONumber}</td>
                    </tr>
                    <tr>
                        <td>{getLocaleValue("technicalManager")}:</td>
                        <td data-cy={"report-table-manager"}>
                            {data.TechnicalManager}
                        </td>
                    </tr>
                    <tr>
                        <td>{getLocaleValue("registeredOwner")}:</td>
                        <td data-cy={"report-table-registered-owner"}>
                            {data.RegisteredOwner}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
